import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Grid, Container, Table,
    TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, CircularProgress, Fade, Avatar,
    Divider, IconButton, Stack, Card, CardContent
} from '@mui/material';
import {
    AccountBalanceWallet as SalaryIcon,
    History as HistoryIcon,
    ArrowBack as ArrowBackIcon,
    TrendingUp as TrendIcon,
    TrendingDown as DeductionIcon,
    Payments as PaymentIcon,
    ReceiptLong as PayslipIcon,
    CalendarMonth as CalendarIcon,
    AddCard as AllowanceIcon,
    LocalAtm as BaseSalaryIcon
} from '@mui/icons-material';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../context/ToastContext';
import { formatDatePart, getNepaliMonthOptions } from '../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -30px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const StatCard = styled(Paper)`
    padding: 24px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 700 !important;
    color: #475569 !important;
    background-color: #f8fafc !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.05em;
`;

const TeacherSalary = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();

    const [salaryInfo, setSalaryInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.school;

    useEffect(() => {
        if (teacherId && schoolId) {
            fetchSalaryHistory();
        }
    }, [teacherId, schoolId]);

    const fetchSalaryHistory = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/Salary/EmployeeHistory/${schoolId}/teacher/${teacherId}`);
            setSalaryInfo(res.data);
        } catch (err) {
            console.error('Error fetching salary:', err);
            showToast('Failed to load salary data', 'error');
        }
        setLoading(false);
    };

    const totals = useMemo(() => {
        if (!salaryInfo?.baseSalary) return { allowances: 0, deductions: 0, net: 0 };

        // Note: The history endpoint might not return the full breakdown of allowances 
        // unless we calculate from the most recent payment or fetch the salary config separately.
        // Assuming we show the structure from the most recent payment or config.

        const history = salaryInfo.paymentHistory || [];
        const latest = history.length > 0 ? history[0] : null;

        if (!latest) {
            return {
                base: salaryInfo.baseSalary,
                allowances: 0,
                deductions: 0,
                net: salaryInfo.baseSalary
            };
        }

        const allowances = Object.values(latest.allowances || {}).reduce((a, b) => a + b, 0);
        const deductions = Object.values(latest.deductions || {}).reduce((a, b) => a + b, 0);

        return {
            base: latest.baseSalary,
            allowances,
            deductions,
            net: latest.netSalary
        };
    }, [salaryInfo]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={() => window.history.back()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                    My Salary
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                    View your earnings, allowances, and payment history.
                                </Typography>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg">
                <Grid container spacing={3}>
                    {/* Summary Section */}
                    <Grid item xs={12} md={4}>
                        <StatCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1' }}><BaseSalaryIcon /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>BASE SALARY</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Rs. {totals.base?.toLocaleString()}</Typography>
                                </Box>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><TrendIcon /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL ALLOWANCES</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Rs. {totals.allowances?.toLocaleString()}</Typography>
                                </Box>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}><DeductionIcon /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL DEDUCTIONS</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Rs. {totals.deductions?.toLocaleString()}</Typography>
                                </Box>
                            </Box>
                        </StatCard>
                    </Grid>

                    {/* Net Salary Highlight */}
                    <Grid item xs={12}>
                        <PremiumCard sx={{
                            p: 4,
                            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important',
                            color: 'white'
                        }}>
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 600 }}>Estimated Net Monthly Earnings</Typography>
                                    <Typography variant="h2" sx={{ fontWeight: 900, mt: 1 }}>Rs. {totals.net?.toLocaleString()}</Typography>
                                </Grid>
                                <Grid item>
                                    <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        <SalaryIcon sx={{ fontSize: 40 }} />
                                    </Avatar>
                                </Grid>
                            </Grid>
                        </PremiumCard>
                    </Grid>

                    {/* Payment History */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6' }}><HistoryIcon /></Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Payment History</Typography>
                        </Box>
                        <PremiumCard sx={{ border: 'none !important' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Month / Year</StyledTableCell>
                                            <StyledTableCell>Amount Paid</StyledTableCell>
                                            <StyledTableCell>Method</StyledTableCell>
                                             <StyledTableCell align="right">Status</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(!salaryInfo?.paymentHistory || salaryInfo.paymentHistory.length === 0) ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                                    <Typography color="textSecondary">No payment records found yet.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            salaryInfo.paymentHistory.map((payment, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CalendarIcon fontSize="small" color="action" />
                                                            {getNepaliMonthOptions().find(m => m.value === payment.month)?.label || payment.month} {payment.year}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: '#10b981' }}>
                                                        Rs. {payment.netSalary?.toLocaleString() || payment.amount?.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={payment.paymentMethod || 'Bank Transfer'} size="small" variant="outlined" sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                                                    </TableCell> 
                                                    <TableCell align="right">
                                                        <Chip
                                                            label="PAID"
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 900,
                                                                bgcolor: '#ecfdf5',
                                                                color: '#059669'
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </PremiumCard>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default TeacherSalary;
